import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react/react/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`Our goal with Primer React is to hit the sweet spot between providing too little and too much styling flexibility; too little and the design system is too rigid, and too much and it becomes too difficult to maintain a consistent style. Our components are designed to cover common usage patterns, but sometimes a component just isn't `}<em parentName="p">{`quite`}</em>{` flexible enough to look the way you need it to look. For those cases, we provide the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop allows ad-hoc styling that is still theme-aware. Declare the styles you want to apply in CamelCase object notation, and try to use theme values in appropriate CSS properties when possible. If you've passed a custom theme using `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` or a `}<inlineCode parentName="p">{`theme`}</inlineCode>{` prop, the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop will honor the custom theme. For more information on theming in Primer React, check out `}<a parentName="p" {...{
        "href": "/primer-theme"
      }}>{`the Primer Theme documentation`}</a>{`.`}</p>
    <h2>{`When to use the `}<inlineCode parentName="h2">{`sx`}</inlineCode>{` prop`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop provides a lot of power, which means it is an easy tool to abuse. To best make use of it, we recommend following these guidelines:`}</p>
    <ul>
      <li parentName="ul">{`Use the `}<inlineCode parentName="li">{`sx`}</inlineCode>{` prop for small stylistic changes to components. For more substantial changes, consider abstracting your style changes into your own wrapper component.`}</li>
      <li parentName="ul">{`Avoid nesting and pseudo-selectors in `}<inlineCode parentName="li">{`sx`}</inlineCode>{` prop values when possible.`}</li>
    </ul>
    <h2>{`Basic example`}</h2>
    <p>{`This example demonstrates applying a bottom border to `}<inlineCode parentName="p">{`Heading`}</inlineCode>{`, a component that does not receive `}<inlineCode parentName="p">{`BORDER`}</inlineCode>{` system props. The `}<inlineCode parentName="p">{`borderBottomWidth`}</inlineCode>{` value comes from `}<inlineCode parentName="p">{`theme.borderWidths`}</inlineCode>{` and `}<inlineCode parentName="p">{`borderBottomColor`}</inlineCode>{` comes from `}<inlineCode parentName="p">{`theme.colors`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<>
  <Heading sx={{paddingBottom: 2}}>Heading</Heading>

  <Heading
    sx={{
      paddingBottom: 2,
      borderBottomWidth: 1,
      borderBottomColor: 'border.default',
      borderBottomStyle: 'solid',
    }}
  >
    Heading with bottom border
  </Heading>
</>
`}</code></pre>
    <h2>{`Responsive values`}</h2>
    <p>{`Just like `}<a parentName="p" {...{
        "href": "https://styled-system.com/responsive-styles"
      }}>{`values passed to system props`}</a>{`, values in the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop can be provided as arrays to provide responsive styling.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<Box
  sx={{
    display: ['flex', 'flex', 'block', 'block'],
    borderRadius: 2,
    padding: 2,
  }}
>
  Responsive background color
</Box>
`}</code></pre>
    <h2>{`Nesting, pseudo-classes, and pseudo-elements`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop also allows for declaring styles based on media queries, pseudo-classes, and pseudo-elements. This example, though contrived, demonstrates the ability:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<Box
  sx={{
    '> *': {
      borderWidth: 1,
      borderColor: 'border.default',
      borderStyle: 'solid',
      borderBottomWidth: 0,
      padding: 2,
      ':last-child': {
        borderBottomWidth: 1,
      },
      ':hover': {
        bg: 'neutral.muted',
      },
    },
  }}
>
  <Box>First</Box>
  <Box>Second</Box>
  <Box>Third</Box>
</Box>
`}</code></pre>
    <h2>{`Value reference`}</h2>
    <ul>
      <li parentName="ul">{`For a reference of possible values for this prop, see the `}<a parentName="li" {...{
          "href": "https://github.com/styled-system/styled-system/blob/master/docs/table.md"
        }}>{`Styled System reference table`}</a>{`.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      